import { useState, useMemo } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { clsx } from 'clsx'
import { getNavigation, NavItem, NavSection } from '@/lib/navigation'
import { useLocale } from '@/i18n'

interface SidebarItemProps {
  item: NavItem
  depth?: number
}

function SidebarItem({ item, depth = 0 }: SidebarItemProps) {
  const location = useLocation()
  const [isOpen, setIsOpen] = useState(() => {
    if (item.items) {
      return item.items.some(
        child => child.href === location.pathname ||
        child.items?.some(grandchild => grandchild.href === location.pathname)
      )
    }
    return false
  })

  const isActive = item.href === location.pathname
  const hasChildren = item.items && item.items.length > 0

  if (hasChildren) {
    return (
      <div className="mb-1">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className={clsx(
            'w-full flex items-center justify-between py-2.5 px-3 text-[15px] font-semibold transition-colors rounded-lg',
            'text-white hover:bg-white/[0.06]',
            isOpen && 'text-white'
          )}
        >
          <span>{item.title}</span>
          {isOpen ? (
            <ChevronDown className="w-4 h-4 text-white/50" />
          ) : (
            <ChevronRight className="w-4 h-4 text-white/50" />
          )}
        </button>
        {isOpen && item.items && (
          <div className="mt-1 ml-3 pl-4 border-l-2 border-white/[0.08] space-y-0.5">
            {item.items.map((child, index) => (
              <SidebarItem key={index} item={child} depth={depth + 1} />
            ))}
          </div>
        )}
      </div>
    )
  }

  if (item.href) {
    return (
      <Link
        to={item.href}
        className={clsx(
          'block py-2 px-3 transition-all rounded-lg',
          depth > 0 ? 'text-[14px]' : 'text-[15px]',
          isActive
            ? 'text-[#FC87B7] bg-[#FC87B7]/10 font-medium'
            : 'text-[#A1A1AA] hover:text-white hover:bg-white/[0.04]'
        )}
      >
        {item.title}
      </Link>
    )
  }

  return null
}

interface SidebarSectionProps {
  section: NavSection
}

function SidebarSection({ section }: SidebarSectionProps) {
  return (
    <div className="mb-8">
      <div className="px-3 mb-3 flex items-center gap-2">
        <span className="text-[11px] font-bold text-[#52525B] uppercase tracking-[0.15em]">
          {section.title}
        </span>
        <div className="flex-1 h-px bg-white/[0.06]" />
      </div>
      <div className="space-y-0.5">
        {section.items.map((item, index) => (
          <SidebarItem key={index} item={item} />
        ))}
      </div>
    </div>
  )
}

interface SidebarProps {
  className?: string
}

export function Sidebar({ className }: SidebarProps) {
  const { locale, t } = useLocale()
  const navigation = useMemo(() => getNavigation(locale), [locale])
  return (
    <aside className={className || "sidebar"} aria-label={t('sidebar.aria')}>
      <nav className="py-2" aria-label={t('sidebar.sectionsAria')}>
        {navigation.map((section, index) => (
          <SidebarSection key={index} section={section} />
        ))}
      </nav>
    </aside>
  )
}
